// priority.js — Priority page sub-renderers + drag-drop reorder.
//
// Mirrors src/policy.js but for the priority ruleset (preemption
// rules: when an agent wants resource X and the current holder is
// Y, do we preempt?).
//
// Same module shape:
//   - applyPriorityListFilter / renderPriorityFilterBar
//   - renderPriorityRuleRow / renderPriorityRuleCard
//   - renderPriorityRuleSentence / describePriorityMatch
//   - wirePriorityRuleDragDrop + 5 private drag handlers
//   - setPriorityReorderCallback for the post-drop re-render
//     (avoids the circular import that direct render-importing
//     would create).

import { priorityState, priorityListFilter } from "./page-state.js";
import { escapeHTML, humanList } from "./util.js";
import { apiFetch } from "./api.js";
import { showToast } from "./ui.js";

// Starter rule names — auto-seeded on first tenant load. Used for
// the "Default" chip annotation.
const STARTER_PRI = new Set([
  "p1-preempts-p2-p3",
  "p2-preempts-p3",
  "interactive-preempts-background",
]);

// applyPriorityListFilter filters the rule list by the user's
// active search text and decision chip.
export function applyPriorityListFilter(rules) {
  const q = priorityListFilter.text.toLowerCase().trim();
  return rules.filter((r) => {
    if (priorityListFilter.decision !== "all") {
      if (priorityListFilter.decision === "preempt" && !r.preempt)
        return false;
      else if (priorityListFilter.decision === "no-op" && r.preempt)
        return false;
      else if (priorityListFilter.decision === "default") {
        if (!STARTER_PRI.has(r.name || "")) return false;
      }
    }
    if (!q) return true;
    const w = r.when || {};
    const hb = r.held_by || {};
    const hay = [
      r.name || "",
      r.reason || "",
      (w.tool || []).join(" "),
      (w.task_priority || []).join(" "),
      (hb.tool || []).join(" "),
      (hb.task_priority || []).join(" "),
    ]
      .join(" ")
      .toLowerCase();
    return hay.indexOf(q) >= 0;
  });
}

// renderPriorityFilterBar — chip bar with per-decision counts +
// search input. Same shape as renderPolicyFilterBar.
export function renderPriorityFilterBar(allRules, filteredRules) {
  const chips = [
    { value: "all", label: "All", count: allRules.length },
    {
      value: "preempt",
      label: "Preempts",
      count: allRules.filter((r) => r.preempt).length,
    },
    {
      value: "no-op",
      label: "No-op",
      count: allRules.filter((r) => !r.preempt).length,
    },
    {
      value: "default",
      label: "Default",
      count: allRules.filter((r) => STARTER_PRI.has(r.name || "")).length,
    },
  ];
  let h =
    '<div style="display: flex; gap: 8px; align-items: center; margin-bottom: 14px; flex-wrap: wrap;">';
  h +=
    '<input type="text" id="priority-list-filter" placeholder="Search rules (name, reason, tool, priority)…" value="' +
    escapeHTML(priorityListFilter.text) +
    '" style="flex: 1; min-width: 200px; padding: 7px 10px; border: 1px solid var(--border); border-radius: var(--r-md); background: var(--bg-panel-solid); color: var(--text); font-size: 13px;" />';
  for (const c of chips) {
    const active = priorityListFilter.decision === c.value;
    h +=
      '<button type="button" data-priority-filter-decision="' +
      escapeHTML(c.value) +
      '" class="' +
      (active ? "btn btn-sm btn-primary" : "btn btn-sm") +
      '" style="font-size: 12px;">' +
      escapeHTML(c.label) +
      (c.count > 0
        ? ' <span style="opacity: 0.7; margin-left: 4px;">' +
          c.count +
          "</span>"
        : "") +
      "</button>";
  }
  h +=
    '<span style="font-size: 12px; color: var(--text-tertiary); margin-left: auto;">' +
    filteredRules.length +
    " of " +
    allRules.length +
    "</span>";
  h += "</div>";
  return h;
}

// describePriorityMatch — turns a Match block into a plain-English
// noun phrase. Falls back to a generic phrase when the match is
// empty. Renders the most-prominent fields (tool, task_priority)
// first; everything else as a qualifier.
function describePriorityMatch(m, fallback) {
  if (!m) return escapeHTML(fallback);
  const parts = [];
  if (Array.isArray(m.task_priority) && m.task_priority.length) {
    parts.push(
      humanList(
        m.task_priority.map((x) => "<code>" + escapeHTML(x) + "</code>"),
      ) + " task",
    );
  }
  if (Array.isArray(m.tool) && m.tool.length) {
    parts.push(
      "from " +
        humanList(m.tool.map((x) => "<code>" + escapeHTML(x) + "</code>")),
    );
  }
  if (Array.isArray(m.agent_id) && m.agent_id.length) {
    const ids = m.agent_id
      .slice(0, 2)
      .map((s) => "<code>" + escapeHTML(s) + "</code>")
      .join(" or ");
    parts.push(
      "by " +
        ids +
        (m.agent_id.length > 2
          ? " (+" + (m.agent_id.length - 2) + ")"
          : ""),
    );
  }
  if (Array.isArray(m.enforcement_level) && m.enforcement_level.length) {
    parts.push(escapeHTML(m.enforcement_level.join("/")) + " enforcement");
  }
  if (Array.isArray(m.laptop_id) && m.laptop_id.length) {
    parts.push("on laptop " + escapeHTML(m.laptop_id[0]));
  }
  if (parts.length === 0) return escapeHTML(fallback);
  return parts.join(" ");
}

// renderPriorityRuleSentence — turns a priority rule into one
// sentence describing what it matches and what it does.
export function renderPriorityRuleSentence(r) {
  const whoWants = describePriorityMatch(r.when, "any task");
  const whoHolds = describePriorityMatch(r.held_by, "any holder");
  const verb = r.preempt
    ? "<strong>preempt</strong> the holder"
    : "<strong>do not preempt</strong> (let the holder finish)";
  let line =
    "When <strong>" +
    whoWants +
    "</strong> is requested and the holder is <strong>" +
    whoHolds +
    "</strong>, " +
    verb +
    ".";
  if (r.min_hold_seconds && r.min_hold_seconds > 0) {
    line +=
      ' <span style="color: var(--text-secondary); font-size: 12.5px;">(min hold ' +
      r.min_hold_seconds +
      "s)</span>";
  }
  return line;
}

// renderPriorityRuleRow — dense, one-line rule display matching
// the Policy page's row layout.
export function renderPriorityRuleRow(r, { readOnly = false, label = "" } = {}) {
  const ruleName = r.name || "";
  const isStarter = STARTER_PRI.has(ruleName);
  const expanded = priorityListFilter.expanded.has(ruleName);
  const sentence = renderPriorityRuleSentence(r);
  const chipClass = r.preempt ? "chip chip-success" : "chip";
  const chipLabel = r.preempt ? "Preempts" : "No-op";

  let h =
    '<div class="priority-rule-row" data-rule-name="' +
    escapeHTML(ruleName) +
    '" ' + (readOnly ? "" : 'draggable="true" ') + 'style="display: flex; align-items: center; gap: 10px; padding: 8px 12px; border-bottom: 1px solid var(--border); background: var(--bg-panel-solid); cursor: ' + (readOnly ? "default" : "grab") + ';">';
  if (!readOnly)
    h +=
      '<span title="Drag to reorder" style="color: var(--text-tertiary); cursor: grab; user-select: none; font-size: 12px; flex-shrink: 0;">⋮⋮</span>';
  h +=
    '<button type="button" class="priority-rule-toggle" data-rule-name="' +
    escapeHTML(ruleName) +
    '" style="background: none; border: none; color: var(--text-tertiary); cursor: pointer; font-size: 10px; padding: 4px; flex-shrink: 0;" aria-label="Expand">' +
    (expanded ? "▾" : "▸") +
    "</button>";
  h +=
    '<strong style="font-size: 13.5px; color: var(--text-display); flex-shrink: 0; min-width: 100px; max-width: 220px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;" title="' +
    escapeHTML(ruleName) +
    '">' +
    escapeHTML(ruleName || label) +
    "</strong>";
  h +=
    '<span class="' +
    chipClass +
    '" style="flex-shrink: 0;">' +
    chipLabel +
    "</span>";
  if (isStarter)
    h +=
      '<span class="chip" style="background: var(--bg-elevated); color: var(--text-secondary); border: 1px solid var(--border); flex-shrink: 0;">Default</span>';
  h +=
    '<div class="priority-rule-sentence" data-rule-name="' +
    escapeHTML(ruleName) +
    '" style="flex: 1; min-width: 0; font-size: 13px; color: var(--text-secondary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">' +
    sentence +
    "</div>";
  if (!readOnly) {
    h +=
      '<button class="btn btn-sm priority-rule-edit-btn" data-rule-name="' +
      escapeHTML(ruleName) +
      '" title="Edit this rule" style="flex-shrink: 0;">Edit</button>';
    h +=
      '<button class="btn btn-sm btn-danger-ghost priority-rule-delete-btn" data-rule-name="' +
      escapeHTML(ruleName) +
      '" title="Delete this rule" style="flex-shrink: 0;">Delete</button>';
  }
  h += "</div>";

  if (expanded) {
    h +=
      '<div style="padding: 12px 16px 16px 50px; border-bottom: 1px solid var(--border); background: var(--bg-elevated); font-size: 13px; color: var(--text); line-height: 1.6;">';
    h += '<div style="margin-bottom: 6px;">' + sentence + "</div>";
    if (r.reason) {
      h +=
        '<div style="margin-top: 6px; color: var(--text-secondary); font-style: italic;">' +
        escapeHTML(r.reason) +
        "</div>";
    }
    h += "</div>";
  }
  return h;
}

// renderPriorityRuleCard — older sentence-shaped card, kept for
// legacy paths.
export function renderPriorityRuleCard(r) {
  const ruleName = r.name || "";
  const isStarter = STARTER_PRI.has(ruleName);
  const sentence = renderPriorityRuleSentence(r);

  let h =
    '<div class="card card-hover priority-rule-card" data-rule-name="' +
    escapeHTML(ruleName) +
    '" draggable="true" style="margin-bottom: 12px; padding: 14px 16px; cursor: grab;">';
  h +=
    '<div style="display: flex; align-items: center; gap: 10px; margin-bottom: 8px;">';
  h +=
    '<span title="Drag to reorder" style="color: var(--text-tertiary); cursor: grab; user-select: none; font-size: 12px;">⋮⋮</span>';
  h +=
    '<strong style="font-size: 14px; color: var(--text-display);">' +
    escapeHTML(ruleName || "(unnamed)") +
    "</strong>";
  const chipClass = r.preempt ? "chip chip-success" : "chip";
  const chipLabel = r.preempt ? "Preempts" : "No-op";
  h +=
    '<span class="' +
    chipClass +
    '" title="' +
    (r.preempt
      ? "Holder is preempted on conflict."
      : "Rule matches but does not preempt — used to whitelist exceptions.") +
    '">' +
    chipLabel +
    "</span>";
  if (isStarter) {
    h +=
      '<span class="chip" title="Starter rule installed by AgentCollision." style="background: var(--bg-elevated); color: var(--text-secondary); border: 1px solid var(--border);">Default</span>';
  }
  h += '<div style="flex: 1;"></div>';
  h +=
    '<button class="btn btn-sm priority-rule-edit-btn" data-rule-name="' +
    escapeHTML(ruleName) +
    '" title="Edit this rule">Edit</button>';
  h +=
    '<button class="btn btn-sm btn-danger-ghost priority-rule-delete-btn" data-rule-name="' +
    escapeHTML(ruleName) +
    '" title="Delete this rule">Delete</button>';
  h += "</div>";

  h +=
    '<div style="font-size: 13.5px; line-height: 1.6; color: var(--text); margin-bottom: 6px;">' +
    sentence +
    "</div>";
  if (r.reason) {
    h +=
      '<div style="font-size: 12.5px; color: var(--text-secondary); font-style: italic;">' +
      escapeHTML(r.reason) +
      "</div>";
  }
  h += "</div>";
  return h;
}

// ---------- drag-to-reorder for priority rules ----------
//
// Module-local state: _priDragSourceName tracks the in-flight drag.
// Reset on dragend. See policy.js for the equivalent comment block.

let _priDragSourceName = null;
let _onPriorityReorderRequest = () => {};

export function setPriorityReorderCallback(fn) {
  _onPriorityReorderRequest = fn;
}

export function wirePriorityRuleDragDrop() {
  const cards = document.querySelectorAll(
    ".priority-rule-card, .priority-rule-row",
  );
  cards.forEach((card) => {
    card.addEventListener("dragstart", onPriDragStart);
    card.addEventListener("dragover", onPriDragOver);
    card.addEventListener("dragleave", onPriDragLeave);
    card.addEventListener("drop", onPriDrop);
    card.addEventListener("dragend", onPriDragEnd);
  });
}

function onPriDragStart(e) {
  const card = e.currentTarget;
  _priDragSourceName = card.dataset.ruleName || "";
  card.style.opacity = "0.4";
  if (e.dataTransfer) {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", _priDragSourceName);
  }
}
function onPriDragOver(e) {
  if (!_priDragSourceName) return;
  e.preventDefault();
  if (e.dataTransfer) e.dataTransfer.dropEffect = "move";
  e.currentTarget.style.borderTop = "2px solid var(--accent)";
}
function onPriDragLeave(e) {
  e.currentTarget.style.borderTop = "";
}
function onPriDragEnd(e) {
  e.currentTarget.style.opacity = "";
  document
    .querySelectorAll(".priority-rule-card, .priority-rule-row")
    .forEach((c) => {
      c.style.borderTop = "";
    });
  _priDragSourceName = null;
}
async function onPriDrop(e) {
  e.preventDefault();
  const target = e.currentTarget;
  target.style.borderTop = "";
  if (!_priDragSourceName) return;
  const targetName = target.dataset.ruleName || "";
  if (!targetName || targetName === _priDragSourceName) return;
  // The full rule list, not the rendered rows: with a filter on, the
  // rows are a subset and the hidden rules got pushed to the end.
  const names = (priorityState.data?.rules || []).map((r) => r.name);
  const srcIdx = names.indexOf(_priDragSourceName);
  const tgtIdx = names.indexOf(targetName);
  if (srcIdx < 0 || tgtIdx < 0) return;
  const [moved] = names.splice(srcIdx, 1);
  const insertAt = tgtIdx <= srcIdx ? tgtIdx : tgtIdx - 1;
  names.splice(insertAt, 0, moved);
  const byName = new Map(
    (priorityState.data?.rules || []).map((r) => [r.name, r]),
  );
  priorityState.data.rules = names.map((n) => byName.get(n)).filter(Boolean);
  await _onPriorityReorderRequest();
  try {
    const resp = await apiFetch("/v1/priority/draft/reorder", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ order: names }),
    });
    if (!resp.ok) throw new Error("HTTP " + resp.status);
    showToast("success", "Order saved.");
  } catch (err) {
    showToast(
      "error",
      "Reorder failed: " +
        (err.message || err) +
        ". Refreshing…",
    );
    priorityState.data = null;
    await _onPriorityReorderRequest();
  }
}
